import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

type CallbackStatus = 'loading' | 'confirming' | 'success' | 'failed';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

/**
 * PaymentCallback — PayStack redirects here after card entry.
 *
 * Route: /payment/callback (no auth required — PayStack may redirect pre-auth)
 * Query params: ?status=success|failed&ref=<reference>
 *
 * On success:
 *   1. Poll GET /api/v1/payments/subscription every 2s (up to 30s) waiting
 *      for tier to go non-free — the webhook is async so the subscription
 *      may not be active yet when we land here (hotfix-1-44 lesson from music).
 *   2. Once confirmed (or timeout), invalidate cache and navigate home.
 *
 * On failure:
 *   - Show retry prompt linking back to /upgrade
 */
const PaymentCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const onConfirmed = () => {
    stopPolling();
    queryClient.invalidateQueries({ queryKey: ['subscription', 'status'] });
    setStatus('success');
    setTimeout(() => navigate('/'), 2000);
  };

  useEffect(() => {
    const rawStatus = searchParams.get('status');

    if (rawStatus !== 'success') {
      setStatus('failed');
      return;
    }

    setStatus('confirming');

    // Poll /payments/subscription until tier goes non-free or timeout.
    // The PayStack webhook is async — subscription may still be pending
    // when the browser lands here (hotfix-1-44 lesson from music).
    pollingRef.current = setInterval(async () => {
      try {
        const sub = await api.getSubscriptionStatus();
        if (sub.tier && sub.tier !== 'free') {
          onConfirmed();
        }
      } catch {
        // ignore transient errors — keep polling
      }
    }, POLL_INTERVAL_MS);

    // Fallback: stop polling after 30s even if not confirmed
    timeoutRef.current = setTimeout(() => {
      stopPolling();
      queryClient.invalidateQueries({ queryKey: ['subscription', 'status'] });
      setStatus('success'); // optimistic — webhook may still arrive
    }, POLL_TIMEOUT_MS);

    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center px-4">
      <div className="w-full max-w-md mx-auto text-center">
        {(status === 'loading' || status === 'confirming') && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 size={48} className="animate-spin text-teal-400" />
            <p className="text-slate-300">
              {status === 'confirming'
                ? 'Confirming your subscription…'
                : 'Processing your payment…'}
            </p>
            {status === 'confirming' && (
              <p className="text-sm text-slate-500">This usually takes a few seconds.</p>
            )}
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center gap-4">
            <CheckCircle size={64} className="text-teal-400" />
            <h1 className="text-2xl font-bold">Subscription activated!</h1>
            <p className="text-slate-400">
              Your plan has been upgraded. Redirecting you to the dashboard…
            </p>
            <div className="mt-2">
              <Loader2 size={18} className="animate-spin text-teal-400 inline mr-2" />
              <span className="text-sm text-slate-500">Redirecting…</span>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="flex flex-col items-center gap-4">
            <XCircle size={64} className="text-red-400" />
            <h1 className="text-2xl font-bold">Payment failed</h1>
            <p className="text-slate-400">
              We couldn't process your payment. Please check your card details and try again.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4 w-full sm:w-auto">
              <button
                onClick={() => navigate('/upgrade')}
                className="w-full sm:w-auto px-6 py-3 bg-teal-500 hover:bg-teal-400 text-slate-900 font-bold rounded-xl transition-colors"
              >
                Try again
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full sm:w-auto px-6 py-3 border border-slate-600 text-slate-300 hover:bg-slate-800 rounded-xl transition-colors"
              >
                Back to dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
